import React, { useEffect, useState } from 'react';
import { X, Check, Send, Clock, User, MessageSquare, Briefcase, Building2, AlertCircle, Plus, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import { Button } from './ui/Button';
export interface Job {
  emoji: string;
  title: string;
  subtitle: string;
  description: string;
  fullDescription?: {
    intro: string;
    tasks: string[];
    requirements: string[];
    callToAction: string;
  };
}
interface JobApplicationFormProps {
  job: Job;
  onClose: () => void;
}
const roleQuestions: Record<string, string[]> = {
  Bagare: ['Har du tidigare erfarenhet av bageri eller konditori? Berätta gärna kort.', 'Vad betyder kvalitet och hantverk för dig?', 'Du kommer in kl 04:00 och upptäcker att jäsningen på degen inte gått som planerat – hela morgonens produktion är påverkad. Vad gör du konkret de första 15 minuterna?', 'Berätta om en gång du märkte att en produkt inte höll den standard du ville ha. Vad gjorde du och varför?'],
  Platschef: ['Har du erfarenhet av både service och att leda ett team? Berätta gärna kort om båda delarna.', 'Hur hanterar du en stressig situation med både personal och kunder?', 'En av dina medarbetare presterar under sin förmåga och stämningen i teamet börjar påverkas. Hur tar du tag i det – steg för steg?', 'Du får feedback från en stamgäst att servicen blivit sämre den senaste veckan. Samtidigt märker du att personalen verkar omotiverad. Vad gör du – och i vilken ordning?'],
  Servicepersonal: ['Har du erfarenhet av service eller kundkontakt? Berätta gärna kort.', 'Beskriv hur du skulle hantera en missnöjd gäst.', 'En gäst frågar om en produkt du inte känner till – du är ensam i kassan och det är kö. Hur agerar du?', 'Ge ett konkret exempel på när du gick utöver det förväntade för att ge någon en bra upplevelse – på jobbet eller privat.']
};
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const textareaClass = 'w-full min-h-[80px] p-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans resize-y text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-448">*</span>;
export function JobApplicationForm({
  job,
  onClose
}: JobApplicationFormProps) {
  const [step, setStep] = useState<'description' | 'form'>('description');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    age: '',
    phone: '',
    email: '',
    city: '',
    workAmount: '',
    hoursPerWeek: '',
    availabilityTimes: '',
    timePreferences: '',
    startDate: '',
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    // Personality questions
    strength: '',
    weakness: '',
    definesYou: '',
    friendDescribes: '',
    employerDescribes: '',
    parentDescribes: '',
    // Previous workplace 1
    prev1_name: '',
    prev1_refName: '',
    prev1_refContact: '',
    prev1_summary: '',
    // Previous workplace 2
    prev2_name: '',
    prev2_refName: '',
    prev2_refContact: '',
    prev2_summary: '',
    // Previous workplace 3
    prev3_name: '',
    prev3_refName: '',
    prev3_refContact: '',
    prev3_summary: '',
    // Education
    edu_name: '',
    edu_school: '',
    edu_year: '',
    edu_description: '',
    // Personal intro
    personalIntro: ''
  });
  const [showWorkplace2, setShowWorkplace2] = useState(false);
  const [showWorkplace3, setShowWorkplace3] = useState(false);
  const [expandedWorkplaces, setExpandedWorkplaces] = useState<Record<number, boolean>>({
    1: true
  });
  const showPartTimeFields = formData.workAmount === 'Deltid' || formData.workAmount === 'Extra / vid behov';
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const questions = roleQuestions[job.title] || roleQuestions['Servicepersonal'];
    // Build a readable message for Web3Forms
    const payload: Record<string, string> = {
      access_key: WEB3FORMS_KEY,
      subject: `Ny ansökan: ${job.title} – ${formData.name}`,
      from_name: formData.name,
      replyto: formData.email,
      // Personal info
      Namn: formData.name,
      Ålder: formData.age,
      Telefon: formData.phone,
      'E-post': formData.email,
      Bostadsort: formData.city,
      'Önskad tjänst': `${job.emoji} ${job.title} (${job.subtitle})`,
      'Hur mycket vill personen jobba': formData.workAmount
    };
    if (showPartTimeFields) {
      payload['Antal timmar per vecka'] = formData.hoursPerWeek;
      payload['Tillgängliga tider'] = formData.availabilityTimes;
    }
    payload['Tidsönskemål'] = formData.timePreferences || '(Ej angivet)';
    payload['Kan börja från'] = formData.startDate || '(Ej angivet)';
    // Role-specific questions
    questions.forEach((q, i) => {
      payload[`Fråga ${i + 1}: ${q}`] = formData[`q${i + 1}`];
    });
    // Personality questions
    payload['Starkaste sida'] = formData.strength;
    payload['Svagaste sida'] = formData.weakness;
    payload['Vad definierar dig som person'] = formData.definesYou;
    payload['Bästa vännen beskriver dig som'] = formData.friendDescribes;
    payload['Arbetsgivaren beskriver dig som'] = formData.employerDescribes;
    payload['Föräldern beskriver dig som'] = formData.parentDescribes;
    // Previous workplaces
    for (let i = 1; i <= 3; i++) {
      const prefix = `prev${i}`;
      const pName = formData[`${prefix}_name`];
      if (pName) {
        payload[`Arbetsplats ${i} – Namn`] = pName;
        payload[`Arbetsplats ${i} – Referens namn`] = formData[`${prefix}_refName`];
        payload[`Arbetsplats ${i} – Referens kontakt`] = formData[`${prefix}_refContact`];
        payload[`Arbetsplats ${i} – Om stället, uppgifter & upplevelse`] = formData[`${prefix}_summary`];
      }
    }
    // Education
    if (formData.edu_name) {
      payload['Utbildning / Kurs'] = formData.edu_name;
      payload['Utbildning – Skola / Anordnare'] = formData.edu_school || '(Ej angivet)';
      payload['Utbildning – År'] = formData.edu_year || '(Ej angivet)';
      payload['Utbildning – Beskrivning'] = formData.edu_description || '(Ej angivet)';
    }
    payload['Personligt intro'] = formData.personalIntro || '(Ej angivet)';
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss direkt.');
      }
    } catch {
      setSubmitError('Kunde inte skicka ansökan. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const questions = roleQuestions[job.title] || roleQuestions['Servicepersonal'];
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-449">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-450">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-451">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-452" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-3" data-id="element-453">
            Tack för din ansökan!
          </h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-454">
            Tack för att du vill jobba hos oss! Vi kommer att gå igenom alla
            ansökningar och höra av oss om vi vill gå vidare till nästa steg.
            Tack!
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-455">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  const SectionHeading = ({
    icon: Icon,
    title
  }: {
    icon: React.ElementType;
    title: string;
  }) => <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-456">
      <Icon className="w-5 h-5 text-[#C5A55A]" data-id="element-457" />
      <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-458">{title}</h4>
    </div>;
  const WorkplaceBlock = ({
    num,
    isRequired
  }: {
    num: number;
    isRequired: boolean;
  }) => {
    const p = `prev${num}`;
    const isExpanded = expandedWorkplaces[num] ?? false;
    const toggleExpand = () => {
      setExpandedWorkplaces(prev => ({
        ...prev,
        [num]: !prev[num]
      }));
    };
    const hasContent = formData[`${p}_name`];
    return <div className="rounded-xl border border-[#C5A55A]/10 overflow-hidden" data-id="element-459">
        <button type="button" onClick={toggleExpand} className="w-full flex items-center justify-between p-4 bg-[#FFF8F0]/60 hover:bg-[#FFF8F0] transition-colors text-left" data-id="element-460">
          <div className="flex items-center gap-2" data-id="element-461">
            <span className="text-xs font-semibold text-[#C5A55A] uppercase tracking-wide" data-id="element-462">
              Arbetsplats {num}
            </span>
            {isRequired && <span className="text-red-500 text-xs" data-id="element-463">*</span>}
            {!isRequired && <span className="text-xs text-[#1A1A1A]/40" data-id="element-464">(valfri)</span>}
            {hasContent && !isExpanded && <span className="text-xs text-[#1A1A1A]/50 ml-2" data-id="element-465">
                – {formData[`${p}_name`]}
              </span>}
          </div>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-[#1A1A1A]/40" data-id="element-466" /> : <ChevronDown className="w-4 h-4 text-[#1A1A1A]/40" data-id="element-467" />}
        </button>

        {isExpanded && <div className="space-y-4 p-5 bg-[#FFF8F0]/30 animate-in slide-in-from-top-1 duration-150" data-id="element-468">
            <div data-id="element-469">
              <label className={labelClass} data-id="element-470">
                Arbetsplatsens namn {isRequired && required}
              </label>
              <input type="text" name={`${p}_name`} required={isRequired} value={formData[`${p}_name`]} onChange={handleChange} className={inputClass} placeholder="T.ex. Café Konditori Solsidan" data-id="element-471" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-472">
              <div data-id="element-473">
                <label className={labelClass} data-id="element-474">
                  Referens – namn (chef/ansvarig) {isRequired && required}
                </label>
                <input type="text" name={`${p}_refName`} required={isRequired} value={formData[`${p}_refName`]} onChange={handleChange} className={inputClass} placeholder="Namn på chef eller ansvarig" data-id="element-475" />
              </div>
              <div data-id="element-476">
                <label className={labelClass} data-id="element-477">
                  Referens – kontaktuppgifter {isRequired && required}
                </label>
                <input type="text" name={`${p}_refContact`} required={isRequired} value={formData[`${p}_refContact`]} onChange={handleChange} className={inputClass} placeholder="Telefon eller e-post" data-id="element-478" />
              </div>
            </div>

            <div data-id="element-479">
              <label className={labelClass} data-id="element-480">
                Beskriv kort stället, dina arbetsuppgifter och din upplevelse{' '}
                {isRequired && required}
              </label>
              <textarea name={`${p}_summary`} required={isRequired} value={formData[`${p}_summary`]} onChange={handleChange} className={textareaClass} placeholder="T.ex. 'Litet familjeägt café i centrala Växjö. Jag jobbade som barista och bakade frallor på morgonen.'" data-id="element-481" />
            </div>
          </div>}
      </div>;
  };
  // === STEP 1: Job Description ===
  if (step === 'description') {
    const fd = job.fullDescription;
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-482">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-483">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-484">
            <div className="flex items-start gap-4" data-id="element-485">
              <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-486">
                {job.emoji}
              </div>
              <div data-id="element-487">
                <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-488">
                  {job.title}
                </h3>
                <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-489">
                  {job.subtitle}
                </p>
                <p className="text-xs text-[#1A1A1A]/50" data-id="element-490">
                  Top Notch Bakery Växjö
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-491">
              <X className="w-6 h-6" data-id="element-492" />
            </button>
          </div>

          {/* Description Body */}
          <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-493">
            {fd ? <div className="space-y-8" data-id="element-494">
                {/* Intro */}
                <div className="space-y-4" data-id="element-495">
                  {fd.intro.split('\n\n').map((paragraph, i) => <p key={i} className="text-[#1A1A1A]/80 leading-relaxed text-[15px]" data-id="element-496">
                      {paragraph}
                    </p>)}
                </div>

                {/* Tasks */}
                <div className="space-y-4" data-id="element-497">
                  <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-498">
                    <Briefcase className="w-5 h-5 text-[#C5A55A]" data-id="element-499" />
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-500">
                      Arbetsuppgifter
                    </h4>
                  </div>
                  <ul className="space-y-2.5 ml-1" data-id="element-501">
                    {fd.tasks.map((task, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#1A1A1A]/75 leading-relaxed" data-id="element-502">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C5A55A] mt-2 shrink-0" data-id="element-503" />
                        {task}
                      </li>)}
                  </ul>
                </div>

                {/* Requirements */}
                <div className="space-y-4" data-id="element-504">
                  <div className="flex items-center gap-3 border-l-4 border-[#C5A55A] pl-3" data-id="element-505">
                    <Check className="w-5 h-5 text-[#C5A55A]" data-id="element-506" />
                    <h4 className="font-serif text-lg text-[#1A1A1A]" data-id="element-507">
                      Vi söker dig som
                    </h4>
                  </div>
                  <ul className="space-y-2.5 ml-1" data-id="element-508">
                    {fd.requirements.map((req, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#1A1A1A]/75 leading-relaxed" data-id="element-509">
                        <Check className="w-4 h-4 text-[#C5A55A] mt-0.5 shrink-0" data-id="element-510" />
                        {req}
                      </li>)}
                  </ul>
                </div>

                {/* Call to action */}
                <div className="p-5 bg-[#FFF8F0] rounded-xl border border-[#C5A55A]/15" data-id="element-511">
                  <p className="text-sm text-[#1A1A1A]/70 leading-relaxed italic" data-id="element-512">
                    {fd.callToAction}
                  </p>
                </div>
              </div> : <p className="text-[#1A1A1A]/70 leading-relaxed" data-id="element-513">
                {job.description}
              </p>}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-514">
            <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-515">
              Stäng
            </Button>
            <Button onClick={() => setStep('form')} className="min-w-[180px]" data-id="element-516">
              <Send className="w-4 h-4 mr-2" data-id="element-517" />
              Gå vidare till ansökan
            </Button>
          </div>
        </div>
      </div>;
  }
  // === STEP 2: Application Form ===
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-518">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-519">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-520">
          <div className="flex items-start gap-4" data-id="element-521">
            <div className="text-4xl bg-[#FFF8F0] w-16 h-16 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-522">
              {job.emoji}
            </div>
            <div data-id="element-523">
              <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-524">
                {job.title}
              </h3>
              <p className="text-[#C5A55A] font-medium text-sm uppercase tracking-wide mb-1" data-id="element-525">
                {job.subtitle}
              </p>
              <p className="text-xs text-[#1A1A1A]/50 hidden sm:block" data-id="element-526">
                Ansökan till Top Notch Bakery Växjö
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-527">
            <X className="w-6 h-6" data-id="element-528" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8" data-id="element-529">
          <form id="application-form" onSubmit={handleSubmit} className="space-y-10" data-id="element-530">
            {/* Section 1: Personal Info */}
            <div className="space-y-5" data-id="element-531">
              <SectionHeading icon={User} title="Personuppgifter" data-id="element-532" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-533">
                <div data-id="element-534">
                  <label htmlFor="name" className={labelClass} data-id="element-535">
                    Namn {required}
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className={inputClass} placeholder="För- och efternamn" data-id="element-536" />
                </div>
                <div data-id="element-537">
                  <label htmlFor="age" className={labelClass} data-id="element-538">
                    Ålder {required}
                  </label>
                  <input type="number" id="age" name="age" required min="16" max="99" value={formData.age} onChange={handleChange} className={inputClass} placeholder="Din ålder" data-id="element-539" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-540">
                <div data-id="element-541">
                  <label htmlFor="phone" className={labelClass} data-id="element-542">
                    Telefonnummer {required}
                  </label>
                  <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} placeholder="070-123 45 67" data-id="element-543" />
                </div>
                <div data-id="element-544">
                  <label htmlFor="email" className={labelClass} data-id="element-545">
                    E-post {required}
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} placeholder="din@epost.se" data-id="element-546" />
                </div>
              </div>
              <div data-id="element-547">
                <label htmlFor="city" className={labelClass} data-id="element-548">
                  Bostadsort {required}
                </label>
                <input type="text" id="city" name="city" required value={formData.city} onChange={handleChange} className={inputClass} placeholder="T.ex. Växjö" data-id="element-549" />
              </div>
            </div>

            {/* Section 2: Work Preferences */}
            <div className="space-y-5" data-id="element-550">
              <SectionHeading icon={Clock} title="Arbetstider & tillgänglighet" data-id="element-551" />

              <div className="space-y-3" data-id="element-552">
                <label className={labelClass} data-id="element-553">
                  Hur mycket vill du jobba? {required}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-554">
                  {['Heltid', 'Halvtid', 'Deltid', 'Extra / vid behov'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-555">
                        <input type="radio" name="workAmount" value={option} required checked={formData.workAmount === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-556" />
                        <span className="text-sm text-[#1A1A1A]" data-id="element-557">{option}</span>
                      </label>)}
                </div>
              </div>

              {/* Conditional: Part-time / Extra details */}
              {showPartTimeFields && <div className="space-y-5 p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 animate-in slide-in-from-top-2 duration-200" data-id="element-558">
                  <div data-id="element-559">
                    <label htmlFor="hoursPerWeek" className={labelClass} data-id="element-560">
                      Ungefär hur många timmar i veckan kan du jobba? {required}
                    </label>
                    <input type="text" id="hoursPerWeek" name="hoursPerWeek" required value={formData.hoursPerWeek} onChange={handleChange} className={inputClass} placeholder="T.ex. 10–15 timmar" data-id="element-561" />
                  </div>
                  <div data-id="element-562">
                    <label htmlFor="availabilityTimes" className={labelClass} data-id="element-563">
                      När är du tillgänglig? {required}
                    </label>
                    <textarea id="availabilityTimes" name="availabilityTimes" required value={formData.availabilityTimes} onChange={handleChange} className={textareaClass} placeholder="T.ex. helger och kvällar, förmiddagar mån–ons, eller skriv fritt vad som passar dig..." data-id="element-564" />
                  </div>
                </div>}

              <div data-id="element-565">
                <label htmlFor="timePreferences" className={labelClass} data-id="element-566">
                  Föredrar du vissa tider framför andra pga din livssituation?
                </label>
                <textarea id="timePreferences" name="timePreferences" value={formData.timePreferences} onChange={handleChange} className={textareaClass} placeholder="T.ex. morgnar passar bäst pga studier, eller inga kvällar pga familj..." data-id="element-567" />
              </div>

              <div data-id="element-568">
                <label htmlFor="startDate" className={labelClass} data-id="element-569">
                  När kan du tänka dig att börja? {required}
                </label>
                <input type="date" id="startDate" name="startDate" required value={formData.startDate} onChange={handleChange} className={inputClass} data-id="element-570" />
              </div>
            </div>

            {/* Section 3: Role Specific Questions */}
            <div className="space-y-5" data-id="element-571">
              <SectionHeading icon={Briefcase} title="Frågor om tjänsten" data-id="element-572" />
              <div className="space-y-5" data-id="element-573">
                {questions.map((q, idx) => <div key={idx} data-id="element-574">
                    <label htmlFor={`q${idx + 1}`} className={labelClass} data-id="element-575">
                      {q} {required}
                    </label>
                    <textarea id={`q${idx + 1}`} name={`q${idx + 1}`} required value={formData[`q${idx + 1}`]} onChange={handleChange} className={textareaClass} data-id="element-576" />
                  </div>)}
              </div>
            </div>

            {/* Section 3b: Personality & Character */}
            <div className="space-y-5" data-id="element-577">
              <SectionHeading icon={User} title="Dig som person" data-id="element-578" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-579">
                <div data-id="element-580">
                  <label htmlFor="strength" className={labelClass} data-id="element-581">
                    Vad är din starkaste sida? {required}
                  </label>
                  <textarea id="strength" name="strength" required value={formData.strength} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '70px'
                }} data-id="element-582" />
                </div>
                <div data-id="element-583">
                  <label htmlFor="weakness" className={labelClass} data-id="element-584">
                    Vad är din svagaste sida? {required}
                  </label>
                  <textarea id="weakness" name="weakness" required value={formData.weakness} onChange={handleChange} className={textareaClass} style={{
                  minHeight: '70px'
                }} data-id="element-585" />
                </div>
              </div>

              <div data-id="element-586">
                <label htmlFor="definesYou" className={labelClass} data-id="element-587">
                  Vad definierar dig som person? {required}
                </label>
                <textarea id="definesYou" name="definesYou" required value={formData.definesYou} onChange={handleChange} className={textareaClass} data-id="element-588" />
              </div>

              <div className="p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 space-y-5" data-id="element-589">
                <p className="text-sm text-[#1A1A1A]/60 italic" data-id="element-590">
                  Beskriv i en mening hur följande personer skulle beskriva dig:
                </p>
                <div data-id="element-591">
                  <label htmlFor="friendDescribes" className={labelClass} data-id="element-592">
                    Din bästa vän {required}
                  </label>
                  <input type="text" id="friendDescribes" name="friendDescribes" required value={formData.friendDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Alltid den som får alla att skratta och känna sig välkomna'" data-id="element-593" />
                </div>
                <div data-id="element-594">
                  <label htmlFor="employerDescribes" className={labelClass} data-id="element-595">
                    Din arbetsgivare {required}
                  </label>
                  <input type="text" id="employerDescribes" name="employerDescribes" required value={formData.employerDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Pålitlig och tar alltid egna initiativ'" data-id="element-596" />
                </div>
                <div data-id="element-597">
                  <label htmlFor="parentDescribes" className={labelClass} data-id="element-598">
                    Din förälder {required}
                  </label>
                  <input type="text" id="parentDescribes" name="parentDescribes" required value={formData.parentDescribes} onChange={handleChange} className={inputClass} placeholder="T.ex. 'Envis på bästa sätt och bryr sig alltid om andra'" data-id="element-599" />
                </div>
              </div>
            </div>

            {/* Section 4: Previous Workplaces */}
            <div className="space-y-5" data-id="element-600">
              <SectionHeading icon={Building2} title="Tidigare arbetsplatser" data-id="element-601" />
              <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-602">
                Din senaste arbetsplats är obligatorisk. Lägg till fler om du
                vill.
              </p>
              <div className="space-y-3" data-id="element-603">
                <WorkplaceBlock num={1} isRequired={true} data-id="element-604" />

                {!showWorkplace2 ? <button type="button" onClick={() => {
                setShowWorkplace2(true);
                setExpandedWorkplaces(prev => ({
                  ...prev,
                  2: true
                }));
              }} className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-[#C5A55A]/25 rounded-xl text-xs font-medium text-[#C5A55A]/70 hover:text-[#C5A55A] hover:border-[#C5A55A]/40 hover:bg-[#FFF8F0]/50 transition-colors" data-id="element-605">
                    <Plus className="w-3.5 h-3.5" data-id="element-606" />
                    Lägg till arbetsplats 2
                  </button> : <WorkplaceBlock num={2} isRequired={false} data-id="element-607" />}

                {showWorkplace2 && !showWorkplace3 ? <button type="button" onClick={() => {
                setShowWorkplace3(true);
                setExpandedWorkplaces(prev => ({
                  ...prev,
                  3: true
                }));
              }} className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-[#C5A55A]/25 rounded-xl text-xs font-medium text-[#C5A55A]/70 hover:text-[#C5A55A] hover:border-[#C5A55A]/40 hover:bg-[#FFF8F0]/50 transition-colors" data-id="element-608">
                    <Plus className="w-3.5 h-3.5" data-id="element-609" />
                    Lägg till arbetsplats 3
                  </button> : showWorkplace3 ? <WorkplaceBlock num={3} isRequired={false} data-id="element-610" /> : null}
              </div>
            </div>

            {/* Section 4b: Education */}
            <div className="space-y-5" data-id="element-611">
              <SectionHeading icon={GraduationCap} title="Utbildning & kompetens" data-id="element-612" />
              <p className="text-xs text-[#1A1A1A]/50 -mt-2" data-id="element-613">
                Berätta om utbildning, kurser eller annan kompetensstyrkande
                erfarenhet du vill lyfta fram.
              </p>
              <div className="space-y-4 p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-614">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-615">
                  <div data-id="element-616">
                    <label htmlFor="edu_name" className={labelClass} data-id="element-617">
                      Utbildning / Kurs
                    </label>
                    <input type="text" id="edu_name" name="edu_name" value={formData.edu_name} onChange={handleChange} className={inputClass} placeholder="T.ex. Restaurang & Livsmedel, Barista-kurs" data-id="element-618" />
                  </div>
                  <div data-id="element-619">
                    <label htmlFor="edu_school" className={labelClass} data-id="element-620">
                      Skola / Anordnare
                    </label>
                    <input type="text" id="edu_school" name="edu_school" value={formData.edu_school} onChange={handleChange} className={inputClass} placeholder="T.ex. Linnéuniversitetet, SFI Växjö" data-id="element-621" />
                  </div>
                </div>
                <div data-id="element-622">
                  <label htmlFor="edu_year" className={labelClass} data-id="element-623">
                    År / Period
                  </label>
                  <input type="text" id="edu_year" name="edu_year" value={formData.edu_year} onChange={handleChange} className={`${inputClass} max-w-[200px]`} placeholder="T.ex. 2022–2024" data-id="element-624" />
                </div>
                <div data-id="element-625">
                  <label htmlFor="edu_description" className={labelClass} data-id="element-626">
                    Beskriv kort vad du lärt dig eller varför det är relevant
                  </label>
                  <textarea id="edu_description" name="edu_description" value={formData.edu_description} onChange={handleChange} className={textareaClass} placeholder="T.ex. 'Lärde mig grunderna i livsmedelshygien och bakning. Fick även praktik på lokalt bageri.'" data-id="element-627" />
                </div>
              </div>
            </div>

            {/* Section 5: Personal Intro */}
            <div className="space-y-5" data-id="element-628">
              <SectionHeading icon={MessageSquare} title="Personligt intro" data-id="element-629" />

              <div className="p-5 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10" data-id="element-630">
                <p className="text-sm text-[#1A1A1A]/70 leading-relaxed mb-4" data-id="element-631">
                  Denna ansökan fungerar som ditt CV och personliga brev i ett.
                  Nedan kan du lämna ett kort personligt intro som beskriver mer
                  om dig som person – sådant som inte redan nämnts ovan.
                </p>
                <label htmlFor="personalIntro" className={labelClass} data-id="element-632">
                  Berätta lite mer om dig själv
                </label>
                <textarea id="personalIntro" name="personalIntro" value={formData.personalIntro} onChange={handleChange} className={`${textareaClass} min-h-[120px]`} placeholder="T.ex. intressen, egenskaper, vad som driver dig, eller något annat du vill att vi ska veta om dig..." data-id="element-633" />
              </div>
            </div>

            {/* Error message */}
            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-634">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-635" />
                <p data-id="element-636">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-637">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-638">
            Avbryt
          </Button>
          <Button type="submit" form="application-form" isLoading={isSubmitting} className="min-w-[180px]" data-id="element-639">
            <Send className="w-4 h-4 mr-2" data-id="element-640" />
            Skicka ansökan
          </Button>
        </div>
      </div>
    </div>;
}